import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Robot, CheckCircle, Sparkle, Copy, Check, Gift, Wallet, Lightning } from "@phosphor-icons/react";

const TIER_LABEL = { basic: "Básico", standard: "Padrão", premium: "Premium" };
const TIER_STYLE = {
  basic: "bg-[#EDF3F0] text-[#0F4C3A]",
  standard: "bg-[#FEF3C7] text-[#92400E]",
  premium: "bg-[#F3E8FF] text-[#7C3AED]",
};
const AMOUNT_PRESETS = [30, 50, 100, 200, 500];

export default function CompanyAI() {
  // Config
  const [settings, setSettings] = useState({
    enabled: false, model: "gemini-3-flash", prompt: "", keywords_handoff: "",
  });
  const [saving, setSaving] = useState(false);

  // Catalog + credits
  const [catalog, setCatalog] = useState({ free_trial_messages: 20, models: [] });
  const [credits, setCredits] = useState({
    balance_brl: 0, free_messages_used: 0, free_messages_left: 20,
    total_spent_brl: 0, total_messages: 0,
  });

  // Topup dialog
  const [openTopup, setOpenTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState(50);
  const [purchase, setPurchase] = useState(null);
  const [loadingPix, setLoadingPix] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [s, c, cr] = await Promise.all([
        api.get("/company/ai-settings"),
        api.get("/ai/catalog"),
        api.get("/ai/credits"),
      ]);
      const kws = Array.isArray(s.data.handoff_keywords) ? s.data.handoff_keywords.join(", ") : (s.data.handoff_keywords || "");
      setSettings({
        enabled: !!s.data.enabled,
        model: s.data.model || c.data.default_model || "gemini-3-flash",
        prompt: s.data.prompt || "",
        keywords_handoff: kws,
      });
      setCatalog(c.data);
      setCredits(cr.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const selectedModel = useMemo(
    () => catalog.models.find((m) => m.key === settings.model) || catalog.models[0],
    [catalog.models, settings.model],
  );

  const save = async () => {
    setSaving(true);
    try {
      const kws = settings.keywords_handoff.split(",").map((s) => s.trim()).filter(Boolean);
      await api.put("/company/ai-settings", {
        enabled: settings.enabled,
        model: settings.model,
        prompt: settings.prompt,
        handoff_keywords: kws,
      });
      toast.success("Configuração salva");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openTopupDialog = () => {
    setTopupAmount(50);
    setPurchase(null);
    setCopied(false);
    setOpenTopup(true);
  };

  const generatePix = async () => {
    if (topupAmount < 10) return toast.error("Valor mínimo: R$ 10,00");
    setLoadingPix(true);
    try {
      const { data } = await api.post("/ai/credits/topup", { amount_brl: Number(topupAmount) });
      setPurchase(data);
      startPolling(data.purchase_id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingPix(false);
    }
  };

  const startPolling = (purchaseId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/ai/credits/topup/${purchaseId}`);
        setPurchase(data);
        if (data.status === "approved") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success("Pagamento confirmado! Créditos adicionados.");
          setOpenTopup(false);
          load();
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const copyPix = async () => {
    if (!purchase?.qr_code) return;
    try {
      await navigator.clipboard.writeText(purchase.qr_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Código Pix copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const freeLeft = credits.free_messages_left;
  const inTrial = freeLeft > 0;

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Automação</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1 flex items-center gap-3">
          <Robot size={40} weight="duotone" className="text-[#0F4C3A]" />
          Assistente IA
        </h1>
        <p className="text-sm text-[#5C5C59] mt-2">
          Ligue a IA e escolha o modelo. Cada resposta gerada desconta um valor fixo do seu saldo.
        </p>
      </div>

      {/* Credit balance card */}
      <div className="mb-6 bg-white border border-[#E5E5E0] shadow-brutal p-6" data-testid="ai-credits-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">Saldo atual</p>
            <div className="mt-1 text-4xl font-black tracking-tighter text-[#0F4C3A]" data-testid="ai-balance">
              R$ {credits.balance_brl.toFixed(2).replace(".", ",")}
            </div>
            {inTrial ? (
              <div className="mt-3 inline-flex items-center gap-2 text-xs bg-[#FEF3C7] text-[#92400E] px-2.5 py-1">
                <Gift size={14} weight="bold" />
                Teste grátis: {freeLeft} de {catalog.free_trial_messages} mensagens restantes
              </div>
            ) : (
              <div className="mt-3 text-xs text-[#5C5C59]">
                {credits.total_messages} msg gerada(s) — R$ {credits.total_spent_brl.toFixed(2).replace(".", ",")} gastos no total
              </div>
            )}
          </div>
          <Button
            onClick={openTopupDialog}
            data-testid="ai-recharge-btn"
            className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal"
          >
            <Wallet size={16} weight="bold" className="mr-2" />
            Recarregar via Pix
          </Button>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="mb-6 bg-white border border-[#E5E5E0] shadow-brutal p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">Status</p>
            <div className={`mt-1 text-2xl font-black tracking-tighter ${settings.enabled ? "text-[#0F4C3A]" : "text-[#5C5C59]"}`}>
              {settings.enabled ? "IA ATIVADA" : "IA DESATIVADA"}
            </div>
            <p className="text-xs text-[#5C5C59] mt-1">
              {settings.enabled ? "IA responde novos tickets automaticamente" : "Novos tickets aguardam resposta humana"}
            </p>
          </div>
          <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} data-testid="ai-enabled-switch" className="data-[state=checked]:bg-[#0F4C3A]" />
        </div>
      </div>

      {/* Model catalog */}
      <div className="mb-6 bg-white border border-[#E5E5E0] shadow-brutal p-6">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59] mb-3">Modelo de IA</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ai-model-grid">
          {catalog.models.map((m) => {
            const active = m.key === settings.model;
            return (
              <button
                key={m.key}
                onClick={() => setSettings({ ...settings, model: m.key })}
                data-testid={`ai-model-${m.key}`}
                className={`text-left p-4 border transition-all ${active ? "border-[#0F4C3A] bg-[#EDF3F0] shadow-brutal" : "border-[#E5E5E0] hover:border-[#0F4C3A]/50 bg-white"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Sparkle size={16} weight="fill" className="text-[#0F4C3A]" />
                    <div>
                      <div className="font-bold text-sm">{m.name}</div>
                      <div className="text-[10px] text-[#5C5C59]">{m.provider}</div>
                    </div>
                  </div>
                  {active && <CheckCircle size={20} weight="fill" className="text-[#0F4C3A]" />}
                </div>
                <div className="mt-2 text-xs text-[#1A1A1A]">{m.description}</div>
                <div className="mt-2 text-[10px] text-[#5C5C59]">
                  <span className="font-semibold">Recomendado:</span> {m.recommended_for}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 ${TIER_STYLE[m.tier]}`}>{TIER_LABEL[m.tier]}</span>
                  <span className="text-sm font-black tracking-tight text-[#0F4C3A]">
                    R$ {m.price_brl.toFixed(2).replace(".", ",")}<span className="text-[10px] font-normal text-[#5C5C59]">/msg</span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {selectedModel && (
          <p className="mt-3 text-[11px] text-[#5C5C59]">
            💡 Com <b>R$ 50,00</b> em créditos, você faz aproximadamente <b>{Math.floor(50 / selectedModel.price_brl)}</b> respostas com <b>{selectedModel.name}</b>.
          </p>
        )}
      </div>

      {/* Prompt + handoff keywords */}
      <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6 space-y-5">
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">Instruções para a IA (Prompt)</Label>
          <Textarea
            value={settings.prompt}
            onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
            data-testid="ai-prompt-input"
            className="rounded-none mt-1 min-h-[120px] font-mono text-xs"
          />
          <p className="text-[10px] text-[#5C5C59] mt-1">Descreva o tom, catálogo, políticas — quanto mais detalhado, melhor a IA responde.</p>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">Palavras-chave para transferir ao humano</Label>
          <Input
            value={settings.keywords_handoff}
            onChange={(e) => setSettings({ ...settings, keywords_handoff: e.target.value })}
            data-testid="ai-keywords-input"
            className="rounded-none mt-1"
            placeholder="atendente, humano, pessoa, falar com alguém"
          />
          <p className="text-[10px] text-[#5C5C59] mt-1">Quando o cliente usar qualquer uma dessas palavras, a IA transfere automaticamente para um atendente humano.</p>
        </div>

        <div className="pt-2 border-t border-[#E5E5E0] flex items-center justify-end">
          <Button onClick={save} disabled={saving} data-testid="ai-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
            {saving ? "Salvando…" : "Salvar configuração"}
          </Button>
        </div>
      </div>

      <div className="mt-6 p-5 bg-[#F7F7F5] border-l-[3px] border-[#0F4C3A]">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-[#0F4C3A] flex items-center gap-1.5">
          <Lightning size={12} weight="bold" /> Como funciona
        </p>
        <ul className="mt-2 space-y-1 text-sm text-[#1A1A1A]">
          <li><b>1.</b> Você ganha <b>{catalog.free_trial_messages} mensagens grátis</b> pra testar qualquer modelo.</li>
          <li><b>2.</b> Depois disso, cada resposta desconta o preço do modelo escolhido do seu saldo.</li>
          <li><b>3.</b> Recarregue quando quiser via <b>Pix</b> — cai instantaneamente e seu saldo é atualizado.</li>
          <li><b>4.</b> Todos os modelos rodam direto na plataforma — <b>zero configuração</b>, sem chaves de API.</li>
        </ul>
      </div>

      {/* Topup dialog */}
      <Dialog open={openTopup} onOpenChange={(v) => { setOpenTopup(v); if (!v && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }}>
        <DialogContent className="bg-white rounded-none max-w-md">
          <DialogHeader><DialogTitle>Recarregar Créditos IA</DialogTitle></DialogHeader>
          {!purchase ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Valor</Label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {AMOUNT_PRESETS.map((v) => (
                    <button
                      key={v}
                      onClick={() => setTopupAmount(v)}
                      data-testid={`ai-topup-preset-${v}`}
                      className={`px-2 py-2 text-sm border transition ${topupAmount === v ? "border-[#0F4C3A] bg-[#EDF3F0] font-bold" : "border-[#E5E5E0] hover:border-[#0F4C3A]/50"}`}
                    >
                      R$ {v}
                    </button>
                  ))}
                </div>
                <Input
                  type="number" min={10} max={5000} step={1}
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(Number(e.target.value))}
                  data-testid="ai-topup-amount-input"
                  className="rounded-none mt-2"
                />
                <p className="text-[10px] text-[#5C5C59] mt-1">Mínimo R$ 10,00 — Pagamento via Pix (Mercado Pago).</p>
                {selectedModel && (
                  <p className="text-[11px] text-[#0F4C3A] mt-1">
                    ≈ <b>{Math.floor(topupAmount / selectedModel.price_brl)}</b> mensagens com {selectedModel.name}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">Pagando</div>
                <div className="text-3xl font-black tracking-tighter text-[#0F4C3A]">
                  R$ {purchase.amount_brl.toFixed(2).replace(".", ",")}
                </div>
                <div className="text-xs mt-1 text-[#5C5C59]">
                  Status: <b className={purchase.status === "approved" ? "text-[#0F4C3A]" : "text-[#D97706]"}>{purchase.status}</b>
                </div>
              </div>
              {purchase.qr_code_base64 && (
                <div className="flex justify-center">
                  <img src={`data:image/png;base64,${purchase.qr_code_base64}`} alt="QR Pix" className="w-56 h-56 border border-[#E5E5E0]" data-testid="ai-topup-qr" />
                </div>
              )}
              {purchase.qr_code && (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-semibold">Código Pix Copia e Cola</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input readOnly value={purchase.qr_code} data-testid="ai-topup-code" className="rounded-none text-[10px] font-mono" />
                    <Button onClick={copyPix} data-testid="ai-topup-copy" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                      {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
                    </Button>
                  </div>
                </div>
              )}
              <p className="text-[11px] text-center text-[#5C5C59]">
                Aguardando pagamento… O saldo é creditado automaticamente após confirmação.
              </p>
            </div>
          )}
          <DialogFooter>
            {!purchase ? (
              <>
                <Button variant="ghost" onClick={() => setOpenTopup(false)} className="rounded-none">Cancelar</Button>
                <Button onClick={generatePix} disabled={loadingPix} data-testid="ai-topup-generate-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                  {loadingPix ? "Gerando…" : "Gerar Pix"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => { setOpenTopup(false); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }}
                className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none"
              >
                Fechar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
